/**
 * The filter bar every list page renders.
 *
 * Clients, Leads (both tabs), Tasks and Construction Deadlines each grew their own
 * bar as they were built, so the four ended up with three control heights, two
 * placeholder styles, filters in whatever order they were added, status as pill
 * buttons on one page and a dropdown on the others, and no way to see at a glance
 * that a filter was still applied. This component is the one implementation: the
 * search box first, then the dropdowns in the order they are declared, then any
 * page-specific extras, then a Clear that appears only when something is on.
 *
 * **Why a component rather than shared classes.** Classes only fix how it looks
 * today; the ordering, the "N filters applied" count and the reset are behaviour, and
 * behaviour copied into four files is behaviour that drifts. Adding a filter to a
 * page is now a line in an array, and it cannot land looking different from its
 * neighbours.
 *
 * **Labels.** The dropdowns are unlabelled by design and carry an `aria-label`
 * instead: their first option states what they are ("All statuses", "Any priority"),
 * which is the pattern the CRM pages already used and what keeps the bar one line
 * tall. Anything needing a visible label belongs in `children`.
 */
import { ReactNode } from 'react'

export interface FilterOption {
  value: string
  label: string
}

export interface FilterSelectSpec {
  /** DOM id, also the React key. */
  id: string
  /** Accessible name — "Filter clients by state". Never rendered visually. */
  label: string
  value: string
  onChange: (value: string) => void
  /**
   * Options in display order. The **first** is the neutral one ("All states") unless
   * `neutralValue` says otherwise: neutral is what Clear resets to and what "this
   * filter is not doing anything" means for the applied-filter count.
   */
  options: FilterOption[]
  /**
   * The value that counts as "not filtering", when it is not the first option.
   * The Auto-Discover window defaults to 90 days but reads best in 30/60/90/180
   * order, so its neutral value is not the first option. Without this it would
   * report itself as an active filter forever.
   *
   * (Lead status used to be the other example here. It is now a set of
   * checkboxes in `children` rather than a select, because a lead list can show
   * any combination of statuses — so it no longer needs a neutral value.)
   */
  neutralValue?: string
  /** Optional width class, for a select whose longest option would stretch the bar. */
  className?: string
}

interface FilterBarProps {
  searchValue: string
  onSearchChange: (value: string) => void
  searchPlaceholder: string
  /** Accessible name for the search box — "Search clients". */
  searchLabel: string
  selects?: FilterSelectSpec[]
  /** Page-specific controls (checkboxes, a labelled window select) rendered last. */
  children?: ReactNode
  /**
   * How many of the `children` controls are currently away from their default, so
   * the count and the Clear button account for them. The bar cannot work this out
   * for controls it does not own.
   */
  extraActiveCount?: number
  /**
   * Reset the `children` controls. Called by Clear after the search box and every
   * select have been reset. Omit when there is nothing extra to reset.
   */
  onClearExtras?: () => void
}

export default function FilterBar({
  searchValue,
  onSearchChange,
  searchPlaceholder,
  searchLabel,
  selects = [],
  children,
  extraActiveCount = 0,
  onClearExtras,
}: FilterBarProps) {
  const neutralOf = (s: FilterSelectSpec) => s.neutralValue ?? s.options[0]?.value ?? ''

  const activeCount =
    (searchValue.trim() ? 1 : 0) +
    selects.filter((s) => s.value !== neutralOf(s)).length +
    extraActiveCount

  function clearAll() {
    onSearchChange('')
    // Back to each select's neutral value, not to '': a select whose neutral is
    // "open" or "90 days" would otherwise be reset to a blank row that means
    // something different from what the page loads with.
    selects.forEach((s) => s.onChange(neutralOf(s)))
    onClearExtras?.()
  }

  return (
    <div className="toolbar gap-2 mb-4">
      <input
        type="search"
        className="filter-control w-full sm:w-64"
        placeholder={searchPlaceholder}
        aria-label={searchLabel}
        value={searchValue}
        onChange={(e) => onSearchChange(e.target.value)}
      />

      {selects.map((s) => (
        <select
          key={s.id}
          id={s.id}
          className={`filter-control ${s.className ?? ''}`}
          aria-label={s.label}
          value={s.value}
          onChange={(e) => s.onChange(e.target.value)}
        >
          {s.options.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      ))}

      {children}

      {activeCount > 0 && (
        <button
          type="button"
          onClick={clearAll}
          className="tap gap-1 ml-auto text-sm text-gray-500 hover:text-gray-800 underline"
        >
          Clear {activeCount} filter{activeCount === 1 ? '' : 's'}
        </button>
      )}
    </div>
  )
}
